/**
 * 🔧 Chunk 加载错误处理组件
 * 
 * 问题背景：
 * - 无状态部署时，旧版本的 chunk 文件会被删除
 * - 用户浏览器缓存了旧 HTML，但请求旧 chunks 会导致 503/404 错误
 * - Next.js 抛出 ChunkLoadError 导致页面白屏
 * 
 * 解决方案：
 * - 监听全局错误事件，捕获 ChunkLoadError
 * - 自动跳转到首页，让用户重新开始
 * - 配合 Nginx 缓存策略（HTML 禁用缓存，JS/CSS 7天缓存）
 * 
 * 注意：
 * - 必须是客户端组件（'use client'）
 * - 需要在 RootLayout 中引入
 */

'use client';

import { useEffect } from 'react';

export function ChunkErrorHandler() {
  useEffect(() => {
    // 处理同步错误（通过 window.onerror）
    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.message?.toLowerCase() || '';
      
      // 检测 ChunkLoadError 相关错误
      const isChunkError = 
        errorMessage.includes('chunkloaderror') ||
        errorMessage.includes('loading chunk') ||
        errorMessage.includes('failed to fetch') ||
        event.error?.name === 'ChunkLoadError';
      
      // 检测 503 错误（服务不可用）
      const is503Error = errorMessage.includes('503');
      
      if (isChunkError || is503Error) {
        console.error('[ChunkErrorHandler] 检测到 chunk 加载失败:', {
          message: event.message,
          filename: event.filename,
          error: event.error,
        });
        
        // 避免在首页重复跳转
        if (window.location.pathname !== '/') {
          console.log('[ChunkErrorHandler] 跳转到首页...');
          event.preventDefault();
          
          // 延迟跳转，给用户看到错误提示的时间
          setTimeout(() => {
            window.location.href = '/';
          }, 100);
        }
      }
    };
    
    // 处理异步错误（Promise rejection）
    const handleRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorMessage = error?.message?.toLowerCase() || error?.toString()?.toLowerCase() || '';
      
      const isChunkError = 
        errorMessage.includes('chunkloaderror') ||
        errorMessage.includes('loading chunk') ||
        errorMessage.includes('failed to fetch') ||
        error?.name === 'ChunkLoadError';
      
      if (isChunkError) {
        console.error('[ChunkErrorHandler] 检测到 Promise rejection (chunk error):', error);
        
        if (window.location.pathname !== '/') {
          console.log('[ChunkErrorHandler] 跳转到首页...');
          event.preventDefault();
          
          setTimeout(() => {
            window.location.href = '/';
          }, 100);
        }
      }
    };
    
    // 注册事件监听器
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    
    // 清理函数
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
  
  // 这个组件不渲染任何 UI
  return null;
}

